import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import i18n from '../i18n/config';

type Language = 'ru' | 'en';

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
}

const STORAGE_KEY = 'techlog-lang';

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

function detectLanguage(): Language {
  if (typeof window === 'undefined') return 'ru';
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved === 'en' || saved === 'ru') return saved;
  return 'ru';
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(detectLanguage);

  useEffect(() => {
    i18n.changeLanguage(language);
    document.documentElement.lang = language;
    // Also sync localStorage for LanguageDetector plugin
    try {
      window.localStorage.setItem(STORAGE_KEY, language);
    } catch {
      // ignore quota errors
    }
  }, [language]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
  }, []);

  const value = useMemo<LanguageContextValue>(
    () => ({ language, setLanguage }),
    [language, setLanguage]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}
